import { sfxCore } from "@/lib/celebrate";

const { audio, out, sample, on } = sfxCore;
const ID = "vault";
let noiseBuf = null;

const noiseSrc = (a) => {
  if (!noiseBuf) {
    noiseBuf = a.createBuffer(1, a.sampleRate * 2, a.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  const s = a.createBufferSource(); s.buffer = noiseBuf; return s;
};

// filtered noise burst with a fast attack and exponential decay
const burst = (a, t, { type = "bandpass", freq = 2000, q = 1, vol = 0.2, dur = 0.05, attack = 0.002 } = {}) => {
  const s = noiseSrc(a), f = a.createBiquadFilter(), g = a.createGain(), at = a.currentTime + t;
  f.type = type; f.frequency.value = freq; f.Q.value = q;
  g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(vol, at + attack); g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  s.connect(f).connect(g).connect(out(a)); s.start(at, Math.random() * 1.5, dur + 0.05);
};

// oscillator with pitch sweep
const sweep = (a, t, { from, to = from, dur = 0.2, type = "sine", vol = 0.1, attack = 0.004 } = {}) => {
  const o = a.createOscillator(), g = a.createGain(), at = a.currentTime + t;
  o.type = type; o.frequency.setValueAtTime(from, at); o.frequency.exponentialRampToValueAtTime(Math.max(20, to), at + dur);
  g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(vol, at + attack); g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  o.connect(g).connect(out(a)); o.start(at); o.stop(at + dur + 0.05);
};

const go = (fn) => { if (!on(ID)) return; const a = audio(); if (a) fn(a); };

const click = (a, t, pitch = 1, vol = 1) => {
  burst(a, t, { type: "highpass", freq: 2600 * pitch, q: 6, vol: 0.32 * vol, dur: 0.022 });
  sweep(a, t, { from: 3400 * pitch, to: 2600 * pitch, dur: 0.03, vol: 0.03 * vol });
  sweep(a, t, { from: 220, to: 150, dur: 0.05, type: "triangle", vol: 0.05 * vol });
};

const clunk = (a, t, big = false) => {
  burst(a, t, { type: "lowpass", freq: big ? 500 : 750, q: 1, vol: big ? 0.7 : 0.5, dur: big ? 0.25 : 0.12 });
  sweep(a, t, { from: big ? 120 : 105, to: big ? 38 : 55, dur: big ? 0.45 : 0.25, vol: big ? 0.5 : 0.32 });
  burst(a, t + 0.004, { freq: 3600, q: 4, vol: 0.18, dur: 0.025 });
  burst(a, t + 0.03, { freq: 1400, q: 12, vol: 0.06, dur: big ? 0.35 : 0.18 });
};

const ting = (a, t) => [2637, 3951, 5588].forEach((f, i) => sweep(a, t + i * 0.004, { from: f, to: f * 0.995, dur: 0.7 - i * 0.15, vol: 0.05 / (i + 1) }));

export const vaultDigit = (d) => go((a) => click(a, 0, 0.85 + Number(d) * 0.035));

export const vaultDelete = () => go((a) => { click(a, 0, 0.7, 0.7); click(a, 0.05, 0.62, 0.5); });

export const vaultSpin = (ms = 950) => go((a) => {
  burst(a, 0, { type: "lowpass", freq: 520, vol: 0.05, dur: ms / 1000, attack: 0.08 });
  let t = 0, step = 0.028, i = 0;
  while (t < ms / 1000) { click(a, t, 1 + (i % 3) * 0.04, 0.55 + Math.random() * 0.3); t += step; step *= 1.07; i++; }
  clunk(a, ms / 1000, false);
});

export const vaultReveal = (gold, silver) => go((a) => {
  for (let i = 0; i < gold; i++) clunk(a, i * 0.22);
  for (let i = 0; i < silver; i++) ting(a, gold * 0.22 + i * 0.2);
  if (!gold && !silver) {
    sweep(a, 0, { from: 210, to: 200, dur: 0.18, type: "square", vol: 0.05 });
    sweep(a, 0.22, { from: 170, to: 150, dur: 0.32, type: "square", vol: 0.05 });
    burst(a, 0, { type: "lowpass", freq: 300, vol: 0.35, dur: 0.18 });
  }
});

export const vaultRevealMs = (gold, silver) => (gold || silver ? gold * 220 + silver * 200 : 550);

export const vaultAlarm = () => go((a) => {
  if (sample(a, "alarm", { vol: 0.55, dur: 1.6 })) return;
  [0, 0.8].forEach((t) => sweep(a, t, { from: 620, to: 980, dur: 0.75, type: "sawtooth", vol: 0.06, attack: 0.05 }));
});

export const vaultKey = () => go((a) => { [3520, 4186, 4699].forEach((f, i) => sweep(a, i * 0.07, { from: f, to: f * 0.99, dur: 0.35, vol: 0.04 })); burst(a, 0.2, { freq: 2200, q: 8, vol: 0.12, dur: 0.04 }); });

export const vaultWin = () => go((a) => {
  [0, 0.13, 0.26, 0.39].forEach((t) => clunk(a, t));
  clunk(a, 0.68, true);
  const creak = a.createOscillator(), f = a.createBiquadFilter(), g = a.createGain(), lfo = a.createOscillator(), lg = a.createGain(), t0 = a.currentTime + 0.95;
  creak.type = "sawtooth"; creak.frequency.setValueAtTime(58, t0); creak.frequency.linearRampToValueAtTime(42, t0 + 1.3);
  lfo.frequency.value = 17; lg.gain.value = 9; lfo.connect(lg).connect(creak.frequency);
  f.type = "bandpass"; f.frequency.value = 520; f.Q.value = 5;
  g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(0.09, t0 + 0.2); g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.35);
  creak.connect(f).connect(g).connect(out(a)); creak.start(t0); lfo.start(t0); creak.stop(t0 + 1.4); lfo.stop(t0 + 1.4);
  burst(a, 0.95, { type: "lowpass", freq: 160, vol: 0.25, dur: 1.4, attack: 0.3 });
  setTimeout(() => {
    if (!sample(a, "coin_big", { vol: 0.8 })) [1319, 1568, 2093].forEach((fr, i) => sweep(a, i * 0.08, { from: fr, dur: 0.4, type: "triangle", vol: 0.08 }));
    setTimeout(() => sample(a, "coin", { vol: 0.6, rate: 1.1 }), 260);
    setTimeout(() => { if (!sample(a, "fanfare", { vol: 0.75 })) [523, 659, 784, 1047].forEach((fr, i) => sweep(a, i * 0.11, { from: fr, dur: 0.4, type: "triangle", vol: 0.1 })); }, 380);
  }, 2200);
  navigator.vibrate?.([40, 60, 40, 60, 40, 60, 40, 200, 120]);
});
